import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProfileAccountInfoFromServer } from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { UserInfo } from "api/interfaces/userInterface.interface";
import { AlertServerPayload } from "./stompMapSlice";
import {
  SubscriptionUpdateInterface,
  SUSPENSION_STATUS,
} from "./stompSubscriptionSlice";
import moment from "moment";
import { FIRMWARE_UPGRADE_STATUS } from "api/interfaces/settingsInterface.interface";

export type SetConnectedPayload = {
  user: UserInfo;
  /** true면 최초 연결이 아닌 재연결(네트워크 복구, 탭 복귀 등) */
  isReconnect?: boolean;
};

export interface StompState {
  connected: boolean;
  currentAccount: ProfileAccountInfoFromServer | null;
  currentUser: UserInfo | null;
  recorderListUpdated: Date | null;
  userPermissionUpdated: Date | null;
  userListUpdated: Date | null;
  userGroupUpdated: Date | null;
  reportListUpdated: Date | null;
  reportDetailUpdated: Date | null;
  videoShareListUpdated: Date | null;
  recorderGroupUpdated: Date | null;
  organizationListUpdated: Date | null;
  alertListUpdated: Date | null;
  alertRuleListUpdated: Date | null;
  suspendUpdated: SubscriptionUpdateInterface & {
    status: SUSPENSION_STATUS | null;
  };
  /** 재연결 성공 시점. 최초 connect에서는 null */
  reconnectedDate: Date | null;
  isDeletedOrganization: boolean;
  notiCount: number;
  userUpdatedId: {
    userId: string;
    updatedAt: Date;
  };
  firmwareUpgrade: {
    recorderId: string;
    status: FIRMWARE_UPGRADE_STATUS | null;
    updatedAt: Date | null;
    reason: string | null;
  };
  firmware: Date | null;
  loginAccessUpdated: { date: Date | null; targetAccountId: string };
  needUpdateCurrentAccount: boolean;
}

const initialState: StompState = {
  connected: false,
  currentAccount: null,
  currentUser: null,
  recorderListUpdated: null,
  userPermissionUpdated: null,
  userListUpdated: null,
  userGroupUpdated: null,
  reportListUpdated: null,
  reportDetailUpdated: null,
  videoShareListUpdated: null,
  recorderGroupUpdated: null,
  organizationListUpdated: null,
  alertListUpdated: null,
  alertRuleListUpdated: null,
  suspendUpdated: { updatedDate: null, accountId: "", status: null },
  reconnectedDate: null,
  isDeletedOrganization: false,
  notiCount: 0,
  userUpdatedId: { userId: "", updatedAt: new Date() },
  firmwareUpgrade: {
    recorderId: "",
    status: null,
    updatedAt: null,
    reason: null,
  },
  firmware: null,
  loginAccessUpdated: { date: null, targetAccountId: "" },
  needUpdateCurrentAccount: false,
};

const stompSlice = createSlice({
  name: "stomp",
  initialState,
  reducers: {
    setConnected: (state, action: PayloadAction<SetConnectedPayload>) => {
      const { user, isReconnect } = action.payload;
      state = {
        ...initialState,
        currentAccount: state.currentAccount,
        currentUser: user,
        notiCount: state.notiCount,
        connected: true,
        reconnectedDate: isReconnect ? new Date() : null,
      };
      return state;
    },
    setReconnectedDate: (state, action: PayloadAction<Date | null>) => {
      state.reconnectedDate = action.payload;
      return state;
    },
    setDisconnected: (state) => {
      state = {
        ...initialState,
      };
      return state;
    },
    markStompUnavailable: (state) => {
      state.connected = false;
      return state;
    },
    setCurrentAccount: (state, action) => {
      state.isDeletedOrganization = false;
      state.needUpdateCurrentAccount = false;
      state.currentAccount = action.payload;
      return state;
    },
    updateDeletedOrganization: (state, action: PayloadAction<boolean>) => {
      state.isDeletedOrganization = action.payload;
      return state;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
      return state;
    },
    recorderListUpdated: (state, action) => {
      if (
        state.currentAccount &&
        (state.currentAccount.accountLevel === LEVEL_TYPE.CP ||
          state.currentAccount.accountId === action.payload.accountId)
      ) {
        state.recorderListUpdated = new Date();
        if (
          action.payload.event === "ADD" ||
          action.payload.event === "DELETE"
        ) {
          state.alertListUpdated = new Date();
          state.recorderGroupUpdated = new Date();
          state.reportListUpdated = new Date();
          state.reportDetailUpdated = new Date();
          state.alertRuleListUpdated = new Date();
          state.organizationListUpdated = new Date();
        }
      }
      return state;
    },
    recorderGroupUpdated: (state, action) => {
      if (
        !state.currentAccount ||
        action.payload.accountId !== state.currentAccount.accountId
      ) {
        return state;
      }
      state.recorderGroupUpdated = new Date();
      return state;
    },
    alertListUpdated: (state, action: PayloadAction<AlertServerPayload>) => {
      if (
        state.currentAccount &&
        state.currentAccount.accountId === action.payload.accountId
      ) {
        const payload = action.payload;
        const status = payload.status.toLowerCase();

        if (status !== "warning") {
          state.alertListUpdated = new Date();
        }
      }
      return state;
    },
    alertRuleListUpdated: (state, action) => {
      if (
        state.currentAccount &&
        state.currentAccount.accountId === action.payload.accountId
      ) {
        state.alertRuleListUpdated = new Date();
      }
      return state;
    },

    userGroupUpdated: (state, action) => {
      if (
        !state.currentAccount ||
        action.payload.accountId !== state.currentAccount.accountId
      ) {
        return state;
      }
      state.userGroupUpdated = new Date();
      if (
        action.payload.accountId === state.currentAccount.accountId &&
        !state.currentAccount.isHidden
      ) {
        state.userPermissionUpdated = new Date();
      }
      return state;
    },
    userUpdated: (state, action) => {
      const isCurrentAccount = action.payload.accounts.find(
        (account: {
          accountId: string;
          accountName: string;
          accountNumber: string;
        }) => {
          return account.accountId === state.currentAccount?.accountId;
        }
      );

      state.userUpdatedId = {
        userId: action.payload.userId,
        updatedAt: new Date(),
      };

      if (
        (action.payload.event === "TMPADD" ||
          action.payload.event === "TMPINV") &&
        action.payload.accounts[0]
      ) {
        state.loginAccessUpdated = {
          date: new Date(),
          targetAccountId: action.payload.accounts[0].accountId,
        };
      }

      if (!isCurrentAccount) {
        return state;
      }

      if (action.payload.userId === state.currentUser?.userId) {
        if ((action.payload.changeMessage || "").includes("permission")) {
          state.userPermissionUpdated = new Date();
        }
        if (action.payload.event === "DELETE") {
          state.isDeletedOrganization = true;
          return state;
        }
      }
      if (action.payload.event === "UPDATE") {
        state.needUpdateCurrentAccount = true;
      }

      state.userListUpdated = new Date();
      return state;
    },
    reportUpdated: (state, action) => {
      if (
        state.currentAccount &&
        (state.currentAccount.accountLevel === LEVEL_TYPE.CP ||
          state.currentAccount.accountId === action.payload.accountId)
      ) {
        state.reportListUpdated = new Date();
        if (
          action.payload.event !== "ADD" &&
          action.payload.reportId &&
          window.location.pathname.includes(action.payload.reportId)
        ) {
          state.reportDetailUpdated = new Date();
        }
      }
      return state;
    },
    organizationUpdated: (state, action) => {
      const isCurrentAccount =
        state.currentAccount?.accountId === action.payload.targetAccountId;
      if (isCurrentAccount) {
        if (action.payload.event === "DELETE") {
          state.isDeletedOrganization = true;
          return;
        }
        if (action.payload.event === "UPDATE") {
          state.needUpdateCurrentAccount = true;
        }
        if (
          action.payload.event === "SUSPEND" &&
          action.payload.status !== state.suspendUpdated.status
        ) {
          state.suspendUpdated = {
            updatedDate: new Date(),
            accountId: action.payload.targetAccountId,
            status: action.payload.status as SUSPENSION_STATUS,
          };
          return state;
        }
        if (
          state.currentAccount?.accountLevel === LEVEL_TYPE.CP &&
          (action.payload.event === "ADD" || action.payload.event === "DELETE")
        ) {
          state.reportListUpdated = new Date();
          state.alertListUpdated = new Date();
          state.alertRuleListUpdated = new Date();
        }
      }
      if (
        state.currentAccount?.accountId !== action.payload.targetAccountId &&
        state.currentUser?.email === action.payload.opEmail &&
        action.payload.event === "SUSPEND" &&
        window.location.pathname.includes("/subscription/keys/organization")
      ) {
        return state;
      }

      state.organizationListUpdated = new Date();
      return state;
    },
    videoShareUpdated: (state, action) => {
      if (!state.currentAccount) {
        state.videoShareListUpdated = new Date();
        return state;
      }
      if (action.payload.accountId !== state.currentAccount.accountId) {
        return state;
      }
      state.videoShareListUpdated = new Date();
      return state;
    },
    updateNotiCount: (state, action) => {
      if (action.payload === "increase") {
        state.notiCount = (state.notiCount || 0) + 1;
      } else if (typeof action.payload === "number") {
        state.notiCount = action.payload;
      }
      return state;
    },
    firmwareUpgrade: (state, action) => {
      if (action.payload.kind === "firmwareUpgrade") {
        state.firmwareUpgrade = {
          recorderId: action.payload.recorderId,
          status: action.payload.status ?? null,
          updatedAt: new Date(),
          reason: action.payload.reason ?? null,
        };
        return state;
      }
      if (
        state.currentAccount &&
        action.payload.accountId !== state.currentAccount.accountId
      ) {
        return state;
      }
      state.recorderListUpdated = new Date();

      return state;
    },
  },
});

export const {
  setConnected,
  setDisconnected,
  markStompUnavailable,
  setCurrentAccount,
  setCurrentUser,
  recorderListUpdated,
  userGroupUpdated,
  userUpdated,
  reportUpdated,
  videoShareUpdated,
  recorderGroupUpdated,
  organizationUpdated,
  updateNotiCount,
  alertRuleListUpdated,
  alertListUpdated,
  updateDeletedOrganization,
  firmwareUpgrade,
  setReconnectedDate,
} = stompSlice.actions;
export const stompReducer = stompSlice.reducer;

export const connectStomp = (user: UserInfo): PayloadAction<UserInfo> => ({
  type: "connect",
  payload: user,
});

/** 로그인 시 저장된 connectedUser로 STOMP만 다시 연결 (토큰 갱신 등) */
export const reconnectStomp = () => ({
  type: "reconnect",
});

export const disconnectStomp = (options?: { expectReconnect?: boolean }) => ({
  type: "disconnect" as const,
  payload: { expectReconnect: !!options?.expectReconnect },
});
